import { useState } from 'react';
import type { Aanwezigheid, MyLimosa } from '@/api/administratie';
import { limosaLine, type LimosaTone } from './limosaLine';
import { LimosaUploadSheet } from './LimosaUploadSheet';

const TONE: Record<LimosaTone, string> = {
  groen: 'bg-green-100 text-green-800',
  oranje: 'bg-orange-100 text-orange-800',
  rood: 'bg-red-100 text-red-700',
  blauw: 'bg-blue-100 text-blue-800',
  grijs: 'bg-gray-100 text-td-muted',
};

/**
 * Limosa under "Wie werkt hier vandaag?" on one job card: the status of every person present
 * who needs a Limosa (or of the installer itself when it works alone) on the card's day, with
 * an upload button where one is missing, expired or rejected. Tapping the status
 * always opens that person's details and earlier uploads. People who do not need one get no
 * line, and the card shows nothing when nobody of the login needs one.
 */
export function JobCardLimosa({
  taskId,
  date,
  limosa,
  aanwezigheid,
  onUploaded,
}: {
  taskId: number;
  date: string;
  limosa: MyLimosa;
  aanwezigheid: Aanwezigheid | null;
  onUploaded: () => void;
}) {
  const [sheet, setSheet] = useState<{ partnerId: number; mode: 'upload' | 'details' } | null>(null);
  const { people, records, status } = limosa;
  const statusOf = (id: number) => status.find((s) => s.partnerId === id);

  if (!people.some((p) => statusOf(p.id)?.verplicht)) return null;

  const alone = people.length === 1 && people[0].isSelf;
  const presentIds = new Set(aanwezigheid?.tasks.find((t) => t.taskId === taskId)?.present ?? []);
  const present = alone ? people : people.filter((p) => presentIds.has(p.id));
  const shown = present.filter((p) => statusOf(p.id)?.verplicht);

  let body: React.ReactNode;
  if (present.length === 0) {
    // Nobody can be picked on a day outside the presence window, so asking for it is noise.
    if (aanwezigheid && !aanwezigheid.editable) return null;
    body = <p className="text-xs text-td-muted">Limosa: kies eerst wie er werkt</p>;
  } else if (shown.length === 0) {
    return null;
  } else {
    body = (
      <>
        <p className="text-xs font-semibold text-td-navy">Limosa</p>
        <ul className="mt-1 space-y-1">
          {shown.map((p) => {
            const line = limosaLine(statusOf(p.id), records, date);
            return (
              <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 text-sm" data-testid="limosa-line">
                <span className="text-td-navy">{p.name}</span>
                <span className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSheet({ partnerId: p.id, mode: 'details' })}
                    aria-label={`Limosa details voor ${p.name}: ${line.label}`}
                    className={`min-h-[36px] px-2.5 text-xs font-semibold rounded-full underline decoration-dotted underline-offset-2 ${TONE[line.tone]}`}
                  >
                    {line.label}
                  </button>
                  {line.canUpload && (
                    <button
                      type="button"
                      onClick={() => setSheet({ partnerId: p.id, mode: 'upload' })}
                      aria-label={`Limosa uploaden voor ${p.name}`}
                      className="min-h-[36px] px-3 rounded-lg bg-td-blue text-white text-xs font-semibold"
                    >
                      Uploaden
                    </button>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      </>
    );
  }

  return (
    <div className="px-4 pb-3" data-testid="job-card-limosa">
      {body}
      {sheet && (
        <LimosaUploadSheet
          people={people}
          records={records}
          partnerId={sheet.partnerId}
          taskId={taskId}
          mode={sheet.mode}
          onUploaded={onUploaded}
          onClose={() => setSheet(null)}
        />
      )}
    </div>
  );
}
